// store/slices/dashboardSlice.ts
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dashboardService, {
  EarningsSummary,
  BotEarningsResponse,
} from "../../services/dashboardService";

interface DashboardState {
  earningsSummary: EarningsSummary | null;
  botEarnings: BotEarningsResponse | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  earningsSummary: null,
  botEarnings: null,
  isLoading: false,
  error: null,
};

export const getEarningsSummary = createAsyncThunk(
  "dashboard/getEarningsSummary",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getEarningsSummary();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch earnings summary",
      );
    }
  },
);

export const getBotEarnings = createAsyncThunk(
  "dashboard/getBotEarnings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getBotEarnings();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch bot earnings",
      );
    }
  },
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Earnings Summary
      .addCase(getEarningsSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEarningsSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.earningsSummary = action.payload;
      })
      .addCase(getEarningsSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Bot Earnings
      .addCase(getBotEarnings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBotEarnings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.botEarnings = action.payload;
      })
      .addCase(getBotEarnings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
